import { t } from 'i18next';
import { Link } from 'react-router-dom';

import { Box, Card, Stack, TableRow, useTheme, Checkbox, TableCell, Typography, CardContent, useMediaQuery } from '@mui/material';

import { paths } from 'src/routes/paths';

import { fDate } from 'src/utils/format-time';
import { KanbanColorMap } from 'src/utils/jobs-kanban';

import { ApplicationStage, ApplicationStatus } from 'src/services/applications/applications.types';

import StarRating from 'src/components/rating/star-rating';

import { ApplicationStatusLabel } from 'src/sections/jobs/applications/applications-status-label';

export type ApplicationTableRow = {
  id: string;
  jobId: string;
  candidate_name: string;
  stage: ApplicationStage;
  status: ApplicationStatus;
  rating: number;
  created_at: string;
};

type Props = {
  row: ApplicationTableRow;
  selected: boolean;
  onRowSelected?: (id: string) => void;
  onRatingChange?: (id: string, rating: number) => void;
};

export default function ApplicationsTableRow({ row, selected, onRowSelected, onRatingChange }: Props) {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <Card sx={{ mb: 2, boxShadow: 1, m: 2 }}>
        <CardContent sx={{
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': { pb: 2 }
        }}>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={2}
            alignItems={isMobile ? 'flex-start' : 'center'}
            width="100%"
          >
            {/* Checkbox and Name Section */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{
                minWidth: isMobile ? '100%' : '200px',
                flex: isMobile ? '1' : '0 0 auto'
              }}
            >
              <Link
                to={paths.dashboard.jobs.application(row.jobId, row.id)}
                style={{ textDecoration: 'none' }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.text.primary,
                    '&:hover': { color: theme.palette.primary.main }
                  }}
                >
                  {row.candidate_name}
                </Typography>
              </Link>

              {!!onRowSelected && (
                <Checkbox
                  checked={selected}
                  onClick={() => onRowSelected(row.id)}
                  sx={{ p: 0.5 }}
                />
              )}
            </Stack>

            {/* Info Section */}
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={1}
              alignItems={isMobile ? 'flex-start' : 'center'}
              sx={{
                flex: 1,
                justifyContent: isMobile ? 'flex-start' : 'space-between',
              }}
            >
              {/* Stage */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ minWidth: isMobile ? '100%' : '140px' }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: KanbanColorMap[row.stage],
                    flexShrink: 0
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500
                  }}
                >
                  {t(`enums.application_stage.${row.stage}`)}
                </Typography>
              </Stack>

              {/* Rating */}
              <Box sx={{
                minWidth: isMobile ? '100%' : '120px',
                display: 'flex',
                justifyContent: isMobile ? 'flex-start' : 'center'
              }}>
                <StarRating
                  rating={row.rating}
                  onSelectExcitementRating={(newRating) => onRatingChange?.(row.id, newRating)}
                />
              </Box>

              {/* Date */}
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  minWidth: isMobile ? '100%' : '100px',
                  textAlign: isMobile ? 'left' : 'right'
                }}
              >
                {fDate(row.created_at)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <TableRow hover>
      {!!onRowSelected && (
        <TableCell padding="checkbox">
          <Checkbox checked={selected} onClick={() => onRowSelected(row.id)} />
        </TableCell>
      )}
      <TableCell>
        <Link to={paths.dashboard.jobs.application(row.jobId, row.id)}>
          <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            {row.candidate_name}
          </Typography>
        </Link>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            width={8}
            height={8}
            borderRadius={4}
            sx={{ backgroundColor: KanbanColorMap[row.stage] }}
          />
          {t(`enums.application_stage.${row.stage}`)}
        </Stack>
      </TableCell>
      <TableCell>
        <ApplicationStatusLabel status={row.status} />
      </TableCell>
      <TableCell>
        <StarRating rating={row.rating} onSelectExcitementRating={(newRating) => onRatingChange?.(row.id as string, newRating)} />
      </TableCell>
      <TableCell>{fDate(row.created_at)}</TableCell>
    </TableRow>
  );
}
